// @flow
import { get } from 'lodash';
import type { Action, ContextState, Dispatch, GoogleCalendarStatus } from '@types';
import { Api } from '../../../helpers';
import { router } from '../../../routes/router';
import { dashboardPageUrl } from './user';

// The signed-in doctor's Google Agenda (api: GoogleCalendarController). Connected, the API puts their
// appointments in a "Doutore" calendar of their Google account and gives each online appointment without
// a link of its own a Google Meet link (into its videoUrl, a few seconds after it is saved).

// ------------------------------------
// Constants
// ------------------------------------
export const GOOGLE_CALENDAR_LOADING = 'googleCalendar.GOOGLE_CALENDAR_LOADING';
export const GOOGLE_CALENDAR_LOADED = 'googleCalendar.GOOGLE_CALENDAR_LOADED';
export const GOOGLE_CALENDAR_FAILED = 'googleCalendar.GOOGLE_CALENDAR_FAILED';
export const GOOGLE_CALENDAR_DISCONNECTING = 'googleCalendar.GOOGLE_CALENDAR_DISCONNECTING';
export const GOOGLE_CALENDAR_DISCONNECTED = 'googleCalendar.GOOGLE_CALENDAR_DISCONNECTED';
export const GOOGLE_CALENDAR_DISCONNECT_FAILED = 'googleCalendar.GOOGLE_CALENDAR_DISCONNECT_FAILED';

// axios has no timeout: a status request that never answers must not hold the next ones
export const GOOGLE_CALENDAR_REQUEST_TIMEOUT = 20 * 1000;

const UNAVAILABLE: GoogleCalendarStatus = {
  available: false,
  connected: false,
  email: null,
  connectedAt: null,
  canConnect: false,
  clinicActivated: false,
  upgradeRequired: false,
  canUpgrade: false,
  needsReconnect: false,
};

const statusFrom = (response: Object): GoogleCalendarStatus => {
  const data = get(response, 'googleCalendar') || response;
  return {
    available: get(data, 'available') === true,
    connected: get(data, 'connected') === true,
    email: get(data, 'email') || null,
    connectedAt: get(data, 'connectedAt') || null,
    canConnect: get(data, 'canConnect') === true,
    clinicActivated: get(data, 'clinicActivated') === true,
    upgradeRequired: get(data, 'upgradeRequired') === true,
    canUpgrade: get(data, 'canUpgrade') === true,
    needsReconnect: get(data, 'needsReconnect') === true,
  };
};

const errorStatus = (error: Object): ?number => get(error, 'status') || get(error, ['response', 'status']);
const signedInDoctorId = (state: Object) => get(state, ['context', 'user', 'advancedData', 'doctorId'], null);

// Whether Ajustes offers the screen: to a doctor, when the API says Google Agenda is `available` (it has
// Google's credentials and one of the doctor's clinics has Google Agenda turned on in the admin). To
// connect, or to see the plans that include it (`upgradeRequired`). Not to a secretary (canConnect
// false), nor once the clinic turns it off, unless connected: then the screen still lets them disconnect.
export const offersGoogleCalendar = (status: ?Object, isDoctor: boolean): boolean => Boolean(
  isDoctor && get(status, 'available')
    && (get(status, 'canConnect') || get(status, 'connected') || get(status, 'upgradeRequired')),
);

// The API's page that connects the Google account, signed in with the user's token, on the host of the API this
// build talks to (dashboardPageUrl: production's beta.doutore.com, a staging or local API's own). The user signs into
// Google there and comes back. This URL carries the user's token: it is for the user's browser only.
export const googleCalendarPageUrl = (accessToken: string): string => dashboardPageUrl(accessToken, '/app/google_calendar');

// ------------------------------------
// Actions
// ------------------------------------

// Coming back to the app fires resume, visibilitychange and focus at once: one request for all of them
let statusRequest = null;

// `maxAge` (ms): only when the stored status is older than that (Ajustes' menu, which only needs `available`)
export const fetchGoogleCalendarStatus = ({ maxAge }: { maxAge?: number } = {}) =>
  (dispatch: Dispatch, getState: Function): Promise<void> => {
    if (statusRequest) return statusRequest;

    const lastFetch = get(getState(), ['context', 'googleCalendar', 'lastFetch']);
    if (maxAge && lastFetch && Date.now() - Date.parse(lastFetch) < maxAge) return Promise.resolve();

    const loaded = (status: GoogleCalendarStatus) => dispatch({
      type: GOOGLE_CALENDAR_LOADED, status, doctorId: signedInDoctorId(getState()),
    });

    dispatch({ type: GOOGLE_CALENDAR_LOADING });

    // 404: an API without Google Agenda yet. No report, no not-found page: it just isn't offered.
    statusRequest = Api.get(router.getApiRoute('getGoogleCalendar'), {
      handledStatuses: [404],
      timeout: GOOGLE_CALENDAR_REQUEST_TIMEOUT,
    })
      .then((response) => { loaded(statusFrom(response.data)); })
      .catch((error) => {
        if (errorStatus(error) === 404) loaded(UNAVAILABLE);
        else dispatch({ type: GOOGLE_CALENDAR_FAILED, error: { message: error.message } });
      })
      .finally(() => { statusRequest = null; });

    return statusRequest;
  };

// Resolves whether it was disconnected; a failure's message stays in context.googleCalendar.disconnectError
export const disconnectGoogleCalendar = () =>
  (dispatch: Dispatch, getState: Function): Promise<boolean> => new Promise((resolve) => {
    dispatch({ type: GOOGLE_CALENDAR_DISCONNECTING });

    Api.destroy(router.getApiRoute('disconnectGoogleCalendar'), {
      success: () => {
        dispatch({ type: GOOGLE_CALENDAR_DISCONNECTED, doctorId: signedInDoctorId(getState()) });
        resolve(true);
      },
      error: (error) => {
        dispatch({ type: GOOGLE_CALENDAR_DISCONNECT_FAILED, error: { message: error.message } });
        resolve(false);
      },
    });
  });

// The paywall's Intercom events (api: GoogleCalendarsController#events): seen, and "Fazer Upgrade" tapped.
// Nothing to wait for: Api.post only logs a failure.
export const trackGoogleCalendarEvent = (event: 'paywall_viewed' | 'upgrade_clicked') =>
  (): Promise<void> => Api.post(router.getApiRoute('googleCalendarEvents'), { data: { event } }).then(() => undefined);

export const googleCalendarActions = {
  fetchGoogleCalendarStatus,
  disconnectGoogleCalendar,
  trackGoogleCalendarEvent,
};

// ------------------------------------
// Reducer
// ------------------------------------

// The signed-in doctor in the clinic's list, so their own appointments know at once whether the API
// creates Google Meet links (OnlineModal) instead of waiting for the next clinics refresh. It does only
// for a connected account and in a clinic that has Google Agenda turned on (`clinicActivated`).
const withDoctorConnected = (state: ContextState, doctorId: ?number, connected: boolean): ContextState => (
  doctorId && get(state, ['doctors', 'data', String(doctorId)])
    ? state.setIn(['doctors', 'data', String(doctorId), 'googleCalendarConnected'], connected)
    : state
);

export default function googleCalendarReducer (state: ContextState, action: Action): ContextState {
  switch (action.type) {
    case GOOGLE_CALENDAR_LOADING:
      return state.setIn(['googleCalendar', 'isLoading'], true);

    case GOOGLE_CALENDAR_LOADED:
      return withDoctorConnected(state.set('googleCalendar', {
        ...(get(state, 'googleCalendar') || {}),
        ...action.status,
        isLoading: false,
        error: null,
        lastFetch: new Date().toISOString(),
      }), action.doctorId, action.status.connected && action.status.clinicActivated);

    case GOOGLE_CALENDAR_FAILED:
      return state
        .setIn(['googleCalendar', 'isLoading'], false)
        .setIn(['googleCalendar', 'error'], action.error.message);

    case GOOGLE_CALENDAR_DISCONNECTING:
      return state
        .setIn(['googleCalendar', 'isDisconnecting'], true)
        .setIn(['googleCalendar', 'disconnectError'], null);

    case GOOGLE_CALENDAR_DISCONNECTED:
      return withDoctorConnected(state.set('googleCalendar', {
        ...(get(state, 'googleCalendar') || {}),
        connected: false,
        email: null,
        connectedAt: null,
        isDisconnecting: false,
      }), action.doctorId, false);

    case GOOGLE_CALENDAR_DISCONNECT_FAILED:
      return state
        .setIn(['googleCalendar', 'isDisconnecting'], false)
        .setIn(['googleCalendar', 'disconnectError'], action.error.message);

    default:
      return state;
  }
}
